/**
 * Level / stage geometry contracts — slice 3 (first playable).
 *
 * The **static** definition of an in-stage layout: world units, collision
 * surfaces, spawn points, the exit door, and the fall-out threshold. This is
 * the first concrete instance of the Section-1 level contract the section specs
 * flagged as "contract TBD" (`docs/design/section1/README.md`).
 *
 * Where this lives + why (Architect decision, slice 3):
 *  - Stage geometry is **shared**, not server-only. The **server** needs it to
 *    run the authoritative physics/collision sim; the **client** needs the same
 *    numbers to render the ground/ledge/door and place the camera. A single
 *    shared `LevelDef` makes drift impossible (MASTER_PROMPT §4 rule 2). The
 *    server remains the only authority over *dynamic* state (positions, clear);
 *    the level is *static input* both sides read identically.
 *  - Coordinates are in **tiles** (1 tile = {@link TILE_SIZE} px). The y axis is
 *    **up-positive** in level space (ground top at `y = 0`, ledges have positive
 *    y, the fall-out plane is negative). The client maps level-y → its own
 *    screen space when rendering; the contract stays render-agnostic.
 *
 * Slice-3 scope: only the object subset 1-0 uses — terrain (AABB solids), a
 * spawn cluster, the exit door, and the fall-out threshold. NOT the full
 * link/plate/lever/sign/element set (later slices extend `LevelObjectType`).
 */
/** Pixels per tile. One tile = 64 px (matches the 1-1 design grid). */
export const TILE_SIZE = 64;
/**
 * Authoritative slice-3 physics constants (defaults from the design spec
 * `docs/design/section1/slice3-first-playable.md` "Tunable parameters").
 *
 *  - `tickRate` 30 Hz fixed timestep (Architect call — smooth platformer feel,
 *    cheap for ≤8 small bodies; the Colyseus state patch rate is independent).
 *  - `walkSpeed` 4.85 / `jumpVelocity` 20.8 / `gravity` 93.5 → PLAZA-EXACT feel
 *    (movement-feel-parity.md E1): solo apex body-bottom **1.974** tiles
 *    (~1.54 body, the literal plaza hop), time-to-apex **~233 ms**. The boost
 *    ledge stays solo-impossible (apex 1.974) and a 2-stack (3.474) clears it.
 *    Documented solo-reach primitives under this feel: flat running-hop far-face
 *    = lip + **3.100**; +1-up hop far-face = lip + **2.833**; +0.5-up hop far-face
 *    = lip + **2.966**; 2-stack rider body-bottom peak **3.474**.
 *
 * The Developer may tune these (the Tester verifies the *inequality* "solo
 * fails, 2-stack succeeds", not exact numbers) — but both client and server
 * import THIS constant so a tweak is one edit.
 */
export const PHYSICS = {
    tickRate: 30,
    walkSpeed: 4.85,
    jumpVelocity: 20.8,
    gravity: 93.5,
    bodyDiameter: 1.5,
    maxFallSpeed: 38.1,
    respawnDelay: 0.5,
    spawnStagger: 0.5,
};
/** Minimum present players required to START a co-op stage (D6 — block, not warn). */
export const MIN_PLAYERS_TO_START = 2;
// ---------------------------------------------------------------------------
// Block-grab tuning (slice 4 — the first Absorb form). Both sides import these.
// ---------------------------------------------------------------------------
/**
 * How close (centre-to-centre, tiles) a player must be to a block to grab it on
 * the Absorb input. The server checks this when it processes a `Grab` intent.
 */
export const BLOCK_GRAB_RANGE = 1.5;
/**
 * Speed (tiles/s) at which a *held* block (and its grabber) move horizontally —
 * deliberately slower than `PHYSICS.walkSpeed` (4.0) so pushing is chunky and
 * positioning reads as a committed action. The grabber's `moveX` drives both the
 * block and the grabber at this speed while holding.
 */
export const BLOCK_PUSH_SPEED = 2.0;
/**
 * Absorb reach (tiles, centre-to-centre) — the **one** documented range for both
 * Absorb target kinds (crate AND teammate). Additive alias of `BLOCK_GRAB_RANGE`
 * (= `LINK_RANGE` = `CHAIN_LINK_RANGE` = 1.5): the verb is **Absorb**, so this
 * names the single reach without splitting the value (absorb-contract §5.1).
 */
export const ABSORB_RANGE = BLOCK_GRAB_RANGE;
/**
 * Absorb carry speed (tiles/s) — the move speed while carrying **anything** via
 * Absorb (crate OR teammate). Additive alias of `BLOCK_PUSH_SPEED` (OD-6 reuse
 * 2.0): documents that the one carry rule (no-jump + carry-slow) applies to every
 * Absorb carry, not just crates (absorb-contract §5.1).
 */
export const ABSORB_CARRY_SPEED = BLOCK_PUSH_SPEED;
/**
 * Trigger radius (tiles, centre-to-centre) of a teaching sign's proximity bubble
 * — the default used unless a `SignObject` overrides it via `triggerRadius`.
 */
export const SIGN_TRIGGER_RADIUS = 1.0;
/**
 * Trigger radius (tiles, centre-to-centre) of a button's press volume (slice 8c)
 * — the default a `ButtonObject` authors with unless it overrides `triggerRadius`.
 * Mirrors the `SIGN_TRIGGER_RADIUS` pattern (the feasibility proof, contract §5.1,
 * uses 1.0 for the 1-4 far-side button).
 */
export const BUTTON_TRIGGER_RADIUS = 1.0;
// ---------------------------------------------------------------------------
// Teammate-link tuning (slice 5 — the SECOND Absorb form). Both sides import.
// ---------------------------------------------------------------------------
//
// The teammate-link is a server-authoritative 2-body **rope/tether** between two
// Pon bodies (D4): a max-length distance *constraint*, not a rigid bar. While the
// two linked bodies are closer than `LINK_MAX_LENGTH` the link applies NO force
// (they move freely); only at the max length does it clamp/hold (the dangle
// radius / anti-fall leash); past `LINK_BREAK_LENGTH` it snaps. These are STATIC
// tuning (like `BLOCK_PUSH_SPEED`) — the only *dynamic* link state is who-is-
// linked-to-whom (`gamestate.ts#PonState.linkedTo`). The tether physics contract
// + the geometry proof live in `docs/design/contracts/slice5-link-protocol.md`.
/**
 * How close (centre-to-centre, tiles) a player must be to a **teammate** to link
 * to them on the Absorb input. Mirrors `BLOCK_GRAB_RANGE` (1.5) — a separate
 * constant so link range can be tuned independently of block-grab range. The
 * server checks this when it processes a `Grab` intent whose nearest in-range
 * Absorb target is a teammate (vs a block).
 */
export const LINK_RANGE = 1.5;
/**
 * Resting (slack) separation of two linked Pon bodies, in tiles centre-to-centre.
 * The link applies **no force** while the bodies are closer than `LINK_MAX_LENGTH`
 * — `LINK_REST_LENGTH` is purely the comfortable resting separation the connector
 * renders at when slack; it does NOT pull the bodies together (the tether is a
 * max-capped leash, not a spring to a rest length, D4).
 */
export const LINK_REST_LENGTH = 1.5;
/**
 * The tether's MAX length (the taut clamp / dangle radius), tiles centre-to-centre.
 * While separation < this the link is slack (no force); AT this distance the link
 * **clamps** — it holds the bodies at most this far apart. This is the leash that
 * makes anti-fall work: a partner who walks off a ledge hangs on a circle of this
 * radius about the grounded anchor's centre (so the dangler is held out over the
 * void, not falling). The geometry proof tunes the chasm so a dangler launched
 * from this taut radius can jump onto the far perch while a solo bare jump cannot.
 */
export const LINK_MAX_LENGTH = 2.5;
/**
 * The tether's BREAK (snap) length, tiles centre-to-centre. If the clamp is
 * overcome and separation exceeds this (e.g. both bodies yanked apart past the
 * leash, or a fast separation the single-tick clamp cannot fully arrest), the
 * link **snaps**: both become free bodies (a dangler over the void then falls and
 * respawns). Larger than `LINK_MAX_LENGTH` so the clamp has headroom to act before
 * the link breaks. Release / fall-out / disconnect also break the link immediately.
 */
export const LINK_BREAK_LENGTH = 3.5;
// ---------------------------------------------------------------------------
// N-Pon chain formation (slice 8b — the human-bridge held in a line). Both
// sides import. The chain generalises the slice-5 2-body `linkedTo` to a
// doubly-linked list (`gamestate.ts#PonState.chainPrev`/`chainNext`); the
// contract is `docs/design/contracts/slice8-human-bridge-protocol.md` §2.2/§3.
// ---------------------------------------------------------------------------
/**
 * Centre-to-centre spacing (tiles) of consecutive members of an **anchored**
 * absorb-chain when the server pins them into a straight standable line (slice
 * 8b). Deliberately **< `PHYSICS.bodyDiameter` (1.5)** so consecutive bodies'
 * solid tops overlap into a continuous walkable surface (the §3.6 one-way solid
 * head-top makes each body standable; the overlap removes any gap a rider could
 * fall through). The feasibility proof (contract §5.1) lays the 5-body bridge at
 * this spacing. Static tuning, like `BLOCK_PUSH_SPEED` — the only *dynamic*
 * chain state is who-is-chained-to-whom (`PonState.chainPrev`/`chainNext`).
 */
export const CHAIN_SPACING = 1.4;
// ---------------------------------------------------------------------------
// STAGE_1_4 player-count gap-scaling (slice 8d — the assembled human-bridge).
// The chasm width SCALES with the party size so the gate stays both
// solo-impossible AND bridge-crossable across the supported range. Curve (G1):
//   gapWidth(P) = STAGE_1_4_BASE_GAP + STAGE_1_4_GAP_PER_PLAYER * (P - STAGE_1_4_PMIN)
// The Architect PROVED (contract §10, faithful sim replica) that the forcing band
// is OPEN only for P >= 5: a 2-body bridge (P=3) does NOT out-reach a solo hop
// (gain 0.000) and a 3-body bridge (P=4) gains only 0.534 — too small for a
// forced-yet-crossable gap. So STAGE_1_4_PMIN is 5 (NOT the GD's proposed 3); the
// server clamps the 1-4 minimum to 5. `GAP_PER_PLAYER 0.5 < CHAIN_SPACING 1.4`, so
// each extra builder adds ~1.4 tiles of bridge reach but only 0.5 of gap -> the
// band only ever widens with P (never a late surprise). `STAGE_1_4_BASE_GAP 6.0`
// is calibrated so gapWidth(6) = 6.5 (the proven baseline, contract §5/§10).
// ---------------------------------------------------------------------------
/** Per-stage minimum solvable party for 1-4 (server clamps; global MIN is 2). */
export const STAGE_1_4_PMIN = 5;
/** Chasm width (tiles) at `STAGE_1_4_PMIN` — the one calibration knob. */
export const STAGE_1_4_BASE_GAP = 6.0;
/** Extra chasm width (tiles) per player above `STAGE_1_4_PMIN` (G1 default). */
export const STAGE_1_4_GAP_PER_PLAYER = 0.5;
// --- 1-4 finale "at door" box (P1-A fix, docs/design/specs/1-4-finale-fix.md) ---
// A full party (up to ROOM_MAX_PLAYERS=8) cannot all be grounded side-by-side on
// the scaled far perch, so overflow Pons stack vertically; a constant-radius
// circle never admits the pile. 1-4 instead uses a height-tolerant box around the
// scaled door (`isInsidePerchDoor`): tight horizontally (stays on the perch),
// generous vertically (admits the base row + ~3 stacked tiers). 1-4 ONLY — every
// other stage keeps the proven circle (`isInsideDoor`). Tunable; perchTopY = 0.
/** Half-width of the 1-4 at-door box (full width 8.0), centred on the scaled door. */
export const STAGE_1_4_DOOR_XBAND = 4.0;
/** How far above the perch top a 1-4 Pon still counts as at-door (admits the pile). */
export const STAGE_1_4_DOOR_HEIGHT_CEIL = 4.0;
/** Slack below the perch top so a settling/landing 1-4 Pon still counts. */
export const STAGE_1_4_DOOR_FLOOR_TOL = 0.5;
/**
 * The 1-4 chasm width (tiles) for a party of `p` players, read once at stage
 * start (server-authoritative; fixed for the run so geometry never shifts
 * mid-stage). Clamps the count to `>= STAGE_1_4_PMIN` so an under-min room (which
 * the server should refuse/warn) never yields a sub-baseline gap. The static
 * `STAGE_1_4` rects below are the P=6 baseline; the server recomputes the chasm /
 * far-perch / button / door from this at runtime (Developer's 8d wiring,
 * contract §10.6).
 */
export function stage14GapWidth(p) {
    const eff = Math.max(p, STAGE_1_4_PMIN);
    return STAGE_1_4_BASE_GAP + STAGE_1_4_GAP_PER_PLAYER * (eff - STAGE_1_4_PMIN);
}
/**
 * How close (centre-to-centre, tiles) a player must be to a **teammate** to
 * absorb-chain them (extend the chain) on the Absorb input (slice 8b). Reuses
 * the slice-5 `LINK_RANGE` value (1.5) — chain-forming uses the same physical
 * Absorb reach as a 2-body link; aliased as a named constant so the chain range
 * could be tuned independently of the slice-5 tether range later.
 */
export const CHAIN_LINK_RANGE = LINK_RANGE;
/**
 * The slice-3 sandbox stage "1-0 — First Steps" (D4 — a dedicated tech-slice
 * layout, NOT a claim that 1-1 is finished). Geometry corrected per the D3 → D2
 * fix (`docs/design/section1/slice3-first-playable.md` "GEOMETRY REQUIREMENT FOR
 * THE ARCHITECT"): the exit door is reachable by **every** player via a
 * solo-jumpable stepped approach, and the old y=3.5 boost ledge is demoted to an
 * **optional off-path lookout** (kept solo-impossible, still 2-stack-reachable).
 *
 * Why the change: with only movement + the §3.6 head-stack in slice-3 scope, a
 * 2-stack boosts only the rider — the base is always stranded. So a clear that
 * required *every* player on the lookout ledge could never fire for ≥2 players
 * (under PLAZA-EXACT feel: solo body-bottom jump peak ≈ 1.974, head-stand boost
 * body-bottom ≈ 3.474, lookout top 3.0). `isStageCleared` is correct; only this
 * static geometry needed fixing. The door now sits on a low stepped plane every
 * player can climb alone; the co-op gate is *gathering* (all-at-door), not a
 * physical boost.
 *
 * Layout (tiles, y up, ground top at y=0; HALF body = 0.75 so a grounded body
 * centre sits 0.75 above the surface it stands on):
 *  - Flat ground x=0..16, top surface y=0 (block body y=-1..0). The whole main
 *    path runs along this; spawn cluster sits on it.
 *  - Stepped approach to the door (ON the path), each step rise ≤ 0.7 tile so a
 *    solo jump (≈1.974-tile body-bottom reach) clears it with margin:
 *      step 1  — top y=0.7  (rise 0.7 from ground)   : block x=8..10  on the ground.
 *      step 2  — top y=1.3  (rise 0.6 from step 1)    : the DOOR PLANE, block x=10..14.
 *  - Exit door centred on the door plane (`at {x:12, y:1.3}`): a player standing
 *    on the plane has body centre (12, 2.05), i.e. 0.75 above — within the 1.5
 *    clear radius across the whole plane top. Reachable by every player solo.
 *  - OPTIONAL head-stack lookout (OFF the door path): the lookout ledge at
 *    x=16..19, a right-side dead-end perch PAST the door. Its left face at
 *    x=16 is a wall a solo jump cannot clear (top 3.0 > solo peak 1.974); a
 *    2-stack rider (body-bottom 3.474 ≥ 3.0) reaches it. Nothing on it gates the
 *    clear; the door is reached (x=12) before this lookout (x≥16), so reaching
 *    the door never requires touching it.
 *  - OPTIONAL Absorb crate (S4): one 1×1 crate on the flat main path (x=5, off
 *    the door route) + a teaching sign at x=4. It is the no-stakes place to first
 *    try Absorb (mirrors the plaza) — Absorb it, carry it, drop it; nothing gates
 *    the clear on it, so 1-0 still completes solo. No forced co-op on 1-0.
 *  - Fall-out: below y=-2.
 */
export const STAGE_1_0_ID = '1-0';
export const STAGE_1_0 = {
    id: STAGE_1_0_ID,
    label: 'First Steps',
    width: 20,
    terrain: [
        // Flat ground (main path): top surface at y=0; block body 1 tile below.
        { type: 'terrain', rect: { x: 0, y: -1, w: 16, h: 1 } },
        // Stepped approach (ON the door path) — each rise ≤0.7t, solo-jumpable:
        // step 1: sits on the ground, top surface y=0.7 (rise 0.7).
        { type: 'terrain', rect: { x: 8, y: 0, w: 2, h: 0.7 } },
        // step 2 = the DOOR PLANE: top surface y=1.3 (rise 0.6 from step 1).
        { type: 'terrain', rect: { x: 10, y: 0, w: 4, h: 1.3 } },
        // Optional head-stack lookout (OFF the door path, right-side dead-end past
        // the door): top surface y=3.0; left face at x=16 is a wall a solo jump
        // cannot clear. Under PLAZA-EXACT feel (E2.1): solo apex 1.974 < 3.0
        // (solo-impossible, margin 1.026), 2-stack rider 3.474 >= 3.0 (reachable,
        // margin 0.474). Only the rect height changed (4.5 -> 4.0); x/y/w unchanged.
        { type: 'terrain', rect: { x: 16, y: -1, w: 3, h: 4.0 } },
    ],
    spawns: [{ type: 'spawn', at: { x: 2, y: 0 } }],
    // Door centred on the door plane (top y=1.3); a standing body centre (y=2.05)
    // is within clearRadius 1.5 across the whole plane top → reachable by all solo.
    door: { type: 'door-exit', at: { x: 12, y: 1.3 }, clearRadius: 1.5 },
    blocks: [
        // OPTIONAL crate (S4): the no-stakes Absorb teaching prop (mirrors the plaza).
        // It sits on the flat main path, off the door route — Absorb it, carry it,
        // drop it for fun; nothing gates the clear on it, so 1-0 still completes solo.
        { type: 'block', id: 'crate-1-0', at: { x: 5, y: 0 }, size: { w: 1, h: 1 } },
    ],
    signs: [
        // Teaching sign next to the optional crate — "try Absorb, no failure cost".
        {
            type: 'sign',
            id: 'sign-absorb-1-0',
            at: { x: 4, y: 0 },
            text: 'Hold Absorb next to the box to pull it. Try it!',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
    ],
    fallOutY: -2,
};
/**
 * Stage **1-1 — "Move, jump, grab"** (slice 4). The first real Section-1 stage:
 * it introduces the first Absorb form (block-grab) and a gap the team cannot
 * cross without it. 1-0 stays registered as the tech sandbox (D2).
 *
 * GEOMETRY PROOF (the slice-3 guardrail — prove the puzzle with numbers before
 * shipping it). Body half-extent HALF = 0.75 (bodyDiameter 1.5). Solo jump from
 * `PHYSICS` (jumpV 20.8, gravity 93.5, walk 4.85, 30 Hz — PLAZA-EXACT): measured
 * body-bottom peak ≈ 1.974 tiles, max FLAT hop far-face (lip→same-height
 * target-left) ≈ lip + 3.100 tiles, max +1 UP hop far-face (lip→+1-higher
 * target-left) ≈ lip + 2.833 tiles. Derivation +
 * tick-stepped numbers: `docs/design/contracts/slice4-blockgrab-protocol.md` §Geometry.
 *
 *   Left → right (tiles, y up, ground top y=0):
 *   - Flat main path: terrain top y=0, x=0..20 (block body y=-1..0). Spawn
 *     cluster x≈2; signs at x=5/10/15/18; the grabbable block spawns at x=17.
 *   - NEAR EDGE (lip): the main floor ends at x=20.
 *   - PIT: **4 tiles wide, 1 tile deep**. Pit floor SOLID, top y=-1, spanning
 *     x=20..24 (so a mistimed jumper lands in the pit, not out — y=-1 > fallOutY
 *     -2 — and climbs back out the near side; gentle). Open above to y=0.
 *   - FAR LEDGE: **+1 tile higher**, top y=1, solid x=24..29 (left face x=24).
 *     Carries the door at x=26.
 *
 *   (A) SOLO-IMPOSSIBLE: from the near lip (x=20), the far ledge left face is at
 *       x=24 → gap 4.0 tiles to a +1-higher ledge. Max solo +1 hop reaches
 *       far-face 20 + 2.833 = 22.833 < 24 (even the flat reach 20 + 3.100 = 23.100
 *       < 24) → a maxed solo jump falls INTO the pit; no player crosses bare. The block
 *       is REQUIRED. (Note: this widens the GD's literal 3-tile pit, where the
 *       3.0 gap was *under* the 3.06 +1-hop reach — i.e. solo-crossable, a
 *       solvability/forced-coop contradiction. Resolved to 4 tiles; see the
 *       contract doc §Resolved-fork. The gap-bridge concept is unchanged.)
 *   (B) SOLVABLE WITH THE BLOCK: pushed into the pit, the 1×1 block rests on the
 *       pit floor with its top FLUSH at y=0 — a single mid-step. The crossing
 *       becomes two hops: near-lip → block-top (FLAT, gap ≤2 ≤ 3.100 ✓) then
 *       block-right-edge → far ledge (+1). Full axis-separated AABB sim (the
 *       slice-3 collision model) confirms BOTH hops succeed with a plain max jump
 *       (no air-control needed) for any block resting with left face in
 *       x∈[21.0, 22.5] — a 1.5-tile-wide forgiving window the pusher easily hits.
 *       Every player (≥2) can cross → the all-at-door clear (unchanged
 *       `isStageCleared`) is achievable.
 *   FORCED-COOPERATION: (1) the gap is solo-impassable bare → the block is
 *       required; (2) min-2 is server-enforced (`MIN_PLAYERS_TO_START`) so solo
 *       runs are refused and the all-at-door clear needs EVERY player across; (3)
 *       a held block can't be jumped with (`jump` disabled while `grabbing`) and
 *       must be DROPPED to become a usable step → it is a SHARED mid-step, not a
 *       personal carried stone. Flow: one player pushes+drops the block flush,
 *       partners cross, the pusher crosses last (the flush block stays). The
 *       honest forcing is the hard "block-required" gate + all-at-door + min-2,
 *       not a claim that a single block geometrically blocks a lone crosser (it
 *       does not — min-2 is what forbids the solo run). See the contract doc.
 */
export const STAGE_1_1_ID = '1-1';
export const STAGE_1_1 = {
    id: STAGE_1_1_ID,
    label: 'Move, jump, grab',
    width: 30,
    terrain: [
        // Flat main path: top surface y=0, x=0..20 (the near lip ends at x=20).
        { type: 'terrain', rect: { x: 0, y: -1, w: 20, h: 1 } },
        // Pit floor (4 wide, 1 deep): solid top y=-1, x=20..24 — catches a mistimed
        // jumper (gentle) and gives the dropped block a floor to rest flush on.
        { type: 'terrain', rect: { x: 20, y: -2, w: 4, h: 1 } },
        // Far ledge (+1 higher): top surface y=1, x=24..29; carries the door.
        { type: 'terrain', rect: { x: 24, y: -1, w: 5, h: 2 } },
    ],
    spawns: [{ type: 'spawn', at: { x: 2, y: 0 } }],
    // Door centred on the far ledge (top y=1); a standing body centre (26, 1.75)
    // is within clearRadius 1.5. Reachable only after crossing on the block.
    door: { type: 'door-exit', at: { x: 26, y: 1 }, clearRadius: 1.5 },
    blocks: [
        // One 1×1 block on the near flat path; plugs the 1-tile pit flush (D4).
        { type: 'block', id: 'block-1', at: { x: 17, y: 0 }, size: { w: 1, h: 1 } },
    ],
    signs: [
        {
            type: 'sign',
            id: 'sign-move',
            at: { x: 5, y: 0 },
            text: 'Tap ← or → to walk. Try it!',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
        {
            type: 'sign',
            id: 'sign-jump',
            at: { x: 10, y: 0 },
            text: 'Press Jump to hop over things.',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
        {
            type: 'sign',
            id: 'sign-grab',
            at: { x: 15, y: 0 },
            text: 'Press Absorb next to the block to grab it. Push it!',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
        {
            type: 'sign',
            id: 'sign-gap',
            at: { x: 18, y: 0 },
            text: 'Push the block into the pit, then hop across together.',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
    ],
    fallOutY: -2,
};
/**
 * Stage **1-1b — "Reach across"** (S3 rework — pinned-teammate-as-step). The
 * dedicated **teammate-Absorb** stage: it teaches the second Absorb form on a
 * **shallow, recoverable dip** crossed by Absorbing a friend into a **pinned
 * standable step** (the 1-4 pin primitive at N=2, with the absorbed teammate body
 * in the 1-1 crate-step role). **No block.** The mechanic already ships (S2:
 * `applyChainFormation` pins the absorbed teammate at the anchor's y; carry
 * no-jump + carry-slow; the absorbed Pon stays fully pinned but can Absorb
 * forward). **S3 is data + signs only** — no sim/server/protocol change. The
 * all-at-door clear (`isStageCleared`/`isInsideDoor`) and every shipped mechanic
 * are reused verbatim. **No `resolveTether` / `linkedTo` / `LINK_*` leash** — the
 * pin formation is the only teammate behaviour now (`linkedTo` stays frozen at '').
 *
 * **Why a pinned step (not a 2-body lip bridge):** the 1-4 proof showed a 2-body
 * bridge spanning one lip-hop equals a solo hop (gain 0.000, see STAGE_1_4), so
 * 1-1b cannot force co-op as a 2-body overhang. It forces co-op as **the absorbed
 * teammate pinned as a discrete mid-step** that splits one impossible hop into two
 * possible ones — exactly the 1-1 crate-step, with the pinned teammate in the
 * crate's role. The forcing combination is the same honest one 1-1 ships: a bare
 * running-hop is solo-impassable (the step is required) + `MIN_PLAYERS_TO_START 2`
 * + the all-at-door clear + carry-no-jump force ≥2 players to each cross. As in
 * 1-1, a lone player *could* walk down the recoverable dip and climb out — that is
 * NOT the forcing claim; the pinned step is the co-op affordance for the intended
 * hop-cross, and the recoverable floor lets the two builders self-recover and
 * reach the door. Full proof: `docs/design/absorb-contract.md` §S3-geom.
 *
 * GEOMETRY (REAL-sim, faithful replica of `sim.ts#stepBody`: HALF 0.75, jumpV
 * 20.8, gravity 93.5, walk 4.85, 30 Hz — PLAZA-EXACT, movement-feel-parity.md
 * E2.2). Primitives: solo body-bottom peak 1.974; max solo flat running-hop
 * far-face = lip + 3.100; +0.5-up hop far-face = lip + 2.966.
 *
 *   Left → right (tiles, y up; ground top at y=0):
 *   - NEAR PATH (main, wide): top y=0, x=0..10 (the near lip is at x=10 — run-up
 *     here). Spawn x=2; two signs at x=4 (absorb) and x=8 (step hint).
 *   - DIP floor (5 wide, 1 deep, RECOVERABLE): top y=-1, x=10..15. A bare hop
 *     lands here (y=-1 > fallOutY -3) and climbs back out — gentle, like 1-1's
 *     pit — and it is the floor the absorber stands on to pin the teammate as a
 *     flush step.
 *   - FAR LEDGE (flat, same height as the near path): top y=0, x=15..21. Carries
 *     the door. +1 above the dip floor (a builder climbs out of the dip onto it).
 *   - Door centred on the far ledge at (18.0, 0), clearRadius 1.5.
 *
 *   How the pinned step forms (shipped `applyChainFormation`): one Pon stands on
 *   the dip floor (top y=-1 → body centre y=-0.25) and Absorbs a teammate. The
 *   member pins at `{ x: ax + i*CHAIN_SPACING(1.4), y: ay }` = at the anchor's y
 *   -0.25, one spacing toward +x → standable top plane = -0.25 + HALF(0.75) =
 *   **+0.5**, a discrete mid-step over the dip.
 *
 *   (A) SOLO-SHORT (forced). From the near lip x=10 the most generous solo running
 *       hop reaches far-face 10 + 3.100 = 13.100; the far-ledge left face is at
 *       15.0 → a bare hop lands **1.900 tiles short** (≥0.5 margin), into the dip,
 *       not on the far ledge. One Pon cannot cross alone; the step is required.
 *   (B) PINNED-STEP CROSSABLE. Absorber stands on the dip floor; the absorbed
 *       teammate pins at top plane +0.5. Hop 1: near lip (y=0) → step top (+0.5),
 *       a short +0.5-up hop (reach far-face = lip + 2.966 = 12.966, caps the
 *       step-left). Hop 2: step top (+0.5) → far ledge (0), a short
 *       slightly-downhill hop (flat reach 3.100). Solvable absorber ax ∈
 *       [10.0, 12.316] (clipped to the dip floor x=10..15); at the centre
 *       (ax≈11.16, pin≈12.56, step-left 11.81) hop1 margin 1.156, hop2 margin
 *       1.410 — both ≥0.5 (the endpoints each have one tight hop → pin centred).
 *   (C) ALL-AT-DOOR CLEAR. Door (18.0, 0) r1.5 on the far ledge (x=15..21): any
 *       body on the far ledge has centre y=0.75 → |Δy|=0.75 ≤ 1.5, x=18.0 mid-ledge
 *       → every crosser reaches the radius. Builders self-recover: the dip is
 *       recoverable (floor y=-1 > fallOutY -3); after the crosser passes, the
 *       absorber releases (the freed teammate drops to the dip floor) and both
 *       builders climb the +1 far wall (peak 1.974 ≫ +1) onto the far ledge → all
 *       gather at the door, the unchanged `isStageCleared` fires.
 */
export const STAGE_1_1B_ID = '1-1b';
export const STAGE_1_1B = {
    id: STAGE_1_1B_ID,
    label: 'Reach across',
    width: 24,
    terrain: [
        // Near path (main, wide): top y=0, x=0..10 (the near lip is at x=10; run-up).
        { type: 'terrain', rect: { x: 0, y: -1, w: 10, h: 1 } },
        // DIP floor (5 wide, 1 deep, RECOVERABLE): solid top y=-1, x=10..15. A bare
        // hop lands here (y=-1 > fallOutY -3) and climbs back out — gentle, like
        // 1-1's pit — and it is the floor the absorber stands on to pin the teammate
        // as a flush step (anchor centre y=-0.25 → pinned step top plane +0.5).
        // PLAZA-EXACT (E2.2): narrowed 6 -> 5 so the far ledge moves in to x=15.
        { type: 'terrain', rect: { x: 10, y: -2, w: 5, h: 1 } },
        // Far ledge (flat, same height as the near path): top y=0, x=15..21 (left
        // face 15). Carries the door. +1 above the dip floor (a builder climbs out).
        // PLAZA-EXACT (E2.2): far-ledge-left 16 -> 15 buys >=0.5 margin on BOTH the
        // pinned-step hops under the snappier jump (solo-short still 1.900 forced).
        { type: 'terrain', rect: { x: 15, y: -1, w: 6, h: 1 } },
    ],
    spawns: [{ type: 'spawn', at: { x: 2, y: 0 } }],
    // Door centred on the far ledge (top y=0); a standing body centre (18.0, 0.75)
    // is 0.75 from the door <= clearRadius 1.5. Reachable by every crosser.
    // PLAZA-EXACT (E2.2): re-centred 18.5 -> 18.0 on the moved ledge (x=15..21).
    door: { type: 'door-exit', at: { x: 18.0, y: 0 }, clearRadius: 1.5 },
    // NO blocks — the STEP is a pinned teammate (Absorb a friend), not a crate.
    signs: [
        {
            type: 'sign',
            id: 'sign-absorb',
            at: { x: 4, y: 0 },
            text: 'Stand by a friend. Press Absorb to pick them up!',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
        {
            type: 'sign',
            id: 'sign-step',
            at: { x: 8, y: 0 },
            text: 'Hold a friend in the dip — their body is a step to hop across!',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
    ],
    // Recoverable dip + a held mover never falls out: -3 (same as 1-4 / old 1-1b).
    fallOutY: -3,
};
/**
 * STAGE 1-4 — Combine: the OWNER's human-bridge (slice 8d). The hard combine of
 * Section 1 — a wide chasm crossed by a SERVER-HELD chain of Pon bodies (the
 * "human bridge"): players absorb-chain into a standable line anchored on the near
 * lip (8a pinned-standable + 8b anchored chain), one crosser walks the tops and
 * jumps to the far perch, presses the far-side button (8c), which spawns a flush
 * bridge across the chasm so EVERY player walks to the door — the unchanged
 * all-at-door clear fires. Elementless.
 *
 * **These rects are the P=6 baseline** (chasm 6.5, contract §5/§10 proven). The
 * chasm width SCALES with the party (`stage14GapWidth`): the server reads `P` once
 * at stage start, clamps `>= STAGE_1_4_PMIN` (5), and recomputes the chasm width /
 * far-perch left face / `button.at.x` / `button.spawns.w` / `door.at.x` from the
 * curve (Developer's 8d runtime wiring, contract §10.6). Geometry is fixed for the
 * run so it never shifts mid-stage.
 *
 * Forced-cooperation (proven, contract §10.4; re-proven under PLAZA-EXACT feel,
 * movement-feel-parity.md E3.2): (A) a lone player canNOT clear the chasm (solo
 * far-face lip+3.100 = 19.100 falls >= 2.90 short of the far-left lip at every
 * P >= 5 — MORE forced under the snappier jump); (B) the (P-1)-body held bridge
 * carries the launch lip far enough that
 * the crosser lands >= 0.4 onto the far perch; (C) the button-spawned flush bridge
 * is what frees the builders (without it the chasm stays solo-impassable). A 2- or
 * 3-player party canNOT build a reaching bridge (a 2-body bridge == a solo hop) ->
 * `STAGE_1_4_PMIN` is 5.
 */
export const STAGE_1_4_ID = '1-4';
export const STAGE_1_4 = {
    id: STAGE_1_4_ID,
    label: 'Human bridge',
    width: 34,
    terrain: [
        // Near path (main, wide): top y=0, x=0..16 (the near lip is at x=16). Spawn
        // cluster x=2; the chain anchor (head) stands with its right-face flush at 16.
        { type: 'terrain', rect: { x: 0, y: -1, w: 16, h: 1 } },
        // Far perch (wide, safe): top y=0, x=22.5..32 (left face = perch-left at the
        // P=6 baseline). Carries the button (x=24) and the door (x=27). The chasm (no
        // floor) spans x=16..22.5 (6.5 tiles at P=6); open to fallOutY -3.
        { type: 'terrain', rect: { x: 22.5, y: -1, w: 9.5, h: 1 } },
    ],
    spawns: [{ type: 'spawn', at: { x: 2, y: 0 } }],
    // Door centred on the far perch (top y=0); a standing body centre (27, 0.75) is
    // within clearRadius 1.5. Solo-UNreachable (the chasm is forced) — reached only
    // after the button spawns the flush bridge. Baseline (P=6) x=27.
    door: { type: 'door-exit', at: { x: 27, y: 0 }, clearRadius: 1.5 },
    // The far-side button: on the far perch at x=24 (perch-left 22.5 + 1.5, so the
    // triggerRadius-1.0 circle sits entirely on solid ground, never over the chasm —
    // contract §10.5). A proximity + on-ground press latches it and spawns the
    // chasm-filling bridge rect (flush, top y=0 -> continuous floor x=0..32).
    buttons: [
        {
            type: 'button',
            id: 'bridge-button',
            at: { x: 24, y: 0 },
            triggerRadius: BUTTON_TRIGGER_RADIUS,
            spawns: { x: 16, y: -1, w: 6.5, h: 1 },
        },
    ],
    // Per-player teaching signs (GD spec docs/design/section1/1-4/README.md), reusing
    // the proven proximity bubble. Position + copy from the GD spec.
    signs: [
        {
            type: 'sign',
            id: 'sign-chain',
            at: { x: 3, y: 0 },
            text: 'Press Absorb on a friend to chain together!',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
        {
            type: 'sign',
            id: 'sign-bridge',
            at: { x: 7, y: 0 },
            text: 'Chain stands at the edge — your bodies make a bridge!',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
        {
            type: 'sign',
            id: 'sign-cross',
            at: { x: 12, y: 0 },
            text: 'One friend walks the bridge, jumps across, hits the button!',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
        {
            type: 'sign',
            id: 'sign-allcross',
            at: { x: 26, y: 0 },
            text: 'Button spawns a bridge — everyone walk across to the door!',
            triggerRadius: SIGN_TRIGGER_RADIUS,
        },
    ],
    // Recoverable chasm: a fall respawns (deeper than slice-3/4's -2, matching 1-1b).
    fallOutY: -3,
};
/** Registry of stages, keyed by id (1-0 sandbox, 1-1 block, 1-1b link, 1-4 human-bridge). */
export const LEVELS = {
    [STAGE_1_0_ID]: STAGE_1_0,
    [STAGE_1_1_ID]: STAGE_1_1,
    [STAGE_1_1B_ID]: STAGE_1_1B,
    [STAGE_1_4_ID]: STAGE_1_4,
};
/**
 * Resolve the level a `stageId` loads. Slice 5 ships the real `1-1b` (the link
 * stage) alongside `1-1` (block) and the `1-0` tech sandbox; an exact id match
 * wins (`'1-1b'` → {@link STAGE_1_1B}, `'1-1'` → {@link STAGE_1_1}, `'1-0'` →
 * {@link STAGE_1_0}). Any other / unknown Section-1 lobby selection resolves to
 * `1-1` (the first real stage); 1-0, 1-1b, and 1-4 remain directly addressable by
 * id (`'1-4'` -> {@link STAGE_1_4}, the assembled human-bridge — slice 8d).
 */
export function resolveLevel(stageId) {
    return LEVELS[stageId] ?? STAGE_1_1;
}
//# sourceMappingURL=level.js.map